import isPropValid from '@emotion/is-prop-valid';
import memoize from '@emotion/memoize';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function isNumber(n) {
  return typeof n === 'number' && !isNaN(n);
}
function isFunction(value) {
  return typeof value === 'function';
}
var defaultBreakpoints = /*#__PURE__*/[40, 52, 64].map(function (n) {
  return n + "em";
});
var defaultTheme = {
  fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 72],
  space: [0, 4, 8, 16, 32, 64, 128, 256, 512]
};

function get(obj, path, fallback) {
  var key = typeof path === 'string' ? path.split('.') : [path];
  var result = obj;

  for (var i = 0; i < key.length; i += 1) {
    if (!result) {
      break;
    }

    result = result[key[i]];
  }

  return result === undefined ? fallback : result;
}
function merge(a, b) {
  var result = _extends({}, a, b);

  Object.keys(result).forEach(function (key) {
    var _Object$assign;

    if (!a[key] || typeof b[key] !== 'object') {
      return;
    }

    Object.assign(result, (_Object$assign = {}, _Object$assign[key] = Object.assign(a[key], b[key]), _Object$assign));
  });
  return result;
}
function sort(obj) {
  var result = {};
  Object.keys(obj).sort(function (a, b) {
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base'
    });
  }).forEach(function (key) {
    result[key] = obj[key];
  });
  return result;
}

function createMediaQuery(n) {
  return "@media screen and (min-width: " + n + ")";
}

function parseResponsiveStyles(mediaQueries, sx, scale, raw, props) {
  var styles = {};
  raw.slice(0, mediaQueries.length).forEach(function (value, i) {
    var media = mediaQueries[i];
    var style = sx(value, scale, props);

    if (!media) {
      styles = _extends({}, styles, style);
    } else {
      var _extends2;

      styles = _extends({}, styles, (_extends2 = {}, _extends2[media] = _extends({}, styles[media], style), _extends2));
    }
  });
  return styles;
}

function parseResponsiveObject(breakpoints, sx, scale, raw, props) {
  var styles = {};
  Object.keys(raw).forEach(function (key) {
    var breakpoint = breakpoints[key]; // FIXME?

    var value = raw[key];
    var style = sx(value, scale, props);

    if (!breakpoint) {
      styles = _extends({}, styles, style);
    } else {
      var _extends3;

      var media = createMediaQuery(breakpoint);
      styles = _extends({}, styles, (_extends3 = {}, _extends3[media] = _extends({}, styles[media], style), _extends3));
    }
  });
  return styles;
}

function createParser(config) {
  var cache = {};

  var parser = function parser(props) {
    var _props$theme;

    var styles = {};
    var shouldSort = false;
    var isCacheDisabled = (_props$theme = props.theme) == null ? void 0 : _props$theme.disableStyledSystemCache;
    Object.keys(props).forEach(function (key) {
      if (!config[key]) {
        return;
      }

      var sx = config[key];
      var raw = props[key];
      var scale = get(props.theme, sx.scale, sx.defaults);

      if (typeof raw === 'object') {
        cache.breakpoints = !isCacheDisabled && cache.breakpoints || get(props.theme, 'breakpoints', defaultBreakpoints);

        if (Array.isArray(raw)) {
          var _cache$breakpoints;

          cache.media = !isCacheDisabled && cache.media || [null].concat(((_cache$breakpoints = cache.breakpoints) != null ? _cache$breakpoints : []).map(createMediaQuery));
          styles = merge(styles, parseResponsiveStyles(cache.media, sx, scale, raw, props));
          return;
        }

        if (raw !== null) {
          var _cache$breakpoints2;

          styles = merge(styles, parseResponsiveObject((_cache$breakpoints2 = cache.breakpoints) != null ? _cache$breakpoints2 : [], sx, scale, raw, props));
          shouldSort = true;
        }

        return;
      }

      styles = _extends({}, styles, sx(raw, scale, props));
    });

    if (shouldSort) {
      styles = sort(styles);
    }

    return styles;
  };

  parser.config = config;
  parser.propNames = Object.keys(config);
  parser.cache = cache;
  var keys = Object.keys(config).filter(function (k) {
    return !['config', 'propNames', 'cache'].includes(k);
  });

  if (keys.length > 1) {
    keys.forEach(function (key) {
      var _createParser;

      parser[key] = createParser((_createParser = {}, _createParser[key] = config[key], _createParser));
    });
  }

  return parser;
}

var compose = function compose() {
  var config = {};

  for (var _len = arguments.length, parsers = new Array(_len), _key = 0; _key < _len; _key++) {
    parsers[_key] = arguments[_key];
  }

  parsers.forEach(function (parser) {
    if (!parser || !parser.config) {
      return;
    }

    config = _extends({}, config, parser.config);
  });
  var parser = createParser(config);
  return parser;
};

function createStyleFunction(_ref) {
  var properties = _ref.properties,
      property = _ref.property,
      scale = _ref.scale,
      _ref$transform = _ref.transform,
      transform = _ref$transform === void 0 ? get : _ref$transform,
      defaultScale = _ref.defaultScale;
  var p = properties || (property ? [property] : []);

  var styleFn = function styleFn(_value, _scale, _props) {
    var result = {};
    var value = transform(_scale, _value, _value, _props);

    if (value === null) {
      return undefined;
    }

    p.forEach(function (prop) {
      result[prop] = value;
    });
    return result;
  };

  styleFn.properties = properties;
  styleFn.property = property;
  styleFn.scale = scale;
  styleFn.transform = transform.name !== 'get' ? transform : undefined;
  styleFn.defaults = defaultScale;
  return styleFn;
}

function system(args) {
  if (args === void 0) {
    args = {};
  }

  var config = {};
  Object.keys(args).forEach(function (key) {
    var argConfig = args[key];

    if (argConfig === false) {
      return;
    } // shorthand definition


    if (argConfig === true) {
      config[key] = createStyleFunction({
        property: key,
        scale: key
      });
      return;
    }

    if (typeof argConfig === 'function') {
      config[key] = argConfig;
      return;
    }

    config[key] = createStyleFunction(argConfig);
  });
  var parser = createParser(config);
  return parser;
}

var pseudoSelectors = {
  _hover: '&:hover, &[data-hover]',
  _active: '&:active, &[data-active]',
  _focus: '&:focus, &[data-focus]',
  _highlighted: '&[data-highlighted]',
  _focusWithin: '&:focus-within',
  _focusVisible: '&:focus-visible',
  _disabled: '&[disabled], &[aria-disabled=true], &[data-disabled]',
  _readOnly: '&[aria-readonly=true], &[readonly], &[data-readonly]',
  _before: '&::before',
  _after: '&::after',
  _empty: '&::empty',
  _expanded: '&[aria-expanded=true], &[data-expanded]',
  _checked: '&:checked, &[aria-checked=true], &[data-checked]',
  _grabbed: '&[aria-grabbed=true], &[data-grabbed]',
  _pressed: '&[aria-pressed=true], &[data-pressed]',
  _invalid: '&[aria-invalid=true], &[data-invalid]',
  _valid: '&[data-valid], &[data-state=valid]',
  _loading: '&[data-loading], &[aria-busy=true]',
  _selected: '&[aria-selected=true], &[data-selected]',
  _hidden: '&[hidden], &[data-hidden]',
  _autofill: '&:-webkit-autofill',
  _even: '&:nth-of-type(even)',
  _odd: '&:nth-of-type(odd)',
  _first: '&:first-of-type',
  _last: '&:last-of-type',
  _notFirst: '&:not(:first-of-type)',
  _notLast: '&:not(:last-of-type)',
  _visited: '&:visited',
  _activeLink: '&[aria-current=page]',
  _indeterminate: '&:indeterminate, &[aria-checked=mixed], &[data-indeterminate]',
  _groupHover: '&[role=group]:hover &, &[role=group][data-hover] &',
  _groupFocus: '&[role=group]:focus &, &[role=group][data-focus] &',
  _groupActive: '&[role=group]:active &, &[role=group][data-active] &',
  _groupDisabled: '&[role=group]:disabled &, [role=group][data-disabled] &',
  _groupInvalid: '&[role=group][data-invalid] &',
  _groupChecked: '&[role=group][data-checked] &',
  _placeholder: '&::placeholder',
  _fullScreen: '&:fullscreen',
  _selection: '&::selection'
};

var config = {
  animation: {
    property: 'animation',
    scale: 'animation.animation'
  },
  animationName: {
    property: 'animationName',
    scale: 'animation.name'
  },
  animationDuration: {
    property: 'animationDuration',
    scale: 'animation.duration'
  },
  animationTimingFunction: {
    property: 'animationTimingFunction',
    scale: 'animation.timingFn'
  },
  animationDelay: {
    property: 'animationDelay',
    scale: 'animation.delay'
  },
  animationIterationCount: {
    property: 'animationIterationCount'
  },
  animationDirection: {
    property: 'animationDirection'
  },
  animationFillMode: {
    property: 'animationFillMode'
  },
  animationPlayState: {
    property: 'animationPlayState'
  }
};
var animation = /*#__PURE__*/system(config);

var config$1 = {
  background: {
    property: 'background',
    scale: 'colors'
  },
  backgroundImage: {
    property: 'backgroundImage'
  },
  backgroundClip: {
    property: 'backgroundClip'
  },
  backgroundSize: {
    property: 'backgroundSize'
  },
  backgroundPosition: {
    property: 'backgroundPosition'
  },
  backgroundRepeat: {
    property: 'backgroundRepeat'
  },
  backgroundAttachment: {
    property: 'backgroundAttachment'
  }
};
config$1.bg = config$1.background;
config$1.bgImage = config$1.backgroundImage;
config$1.bgClip = config$1.backgroundClip;
config$1.bgSize = config$1.backgroundSize;
config$1.bgPosition = config$1.backgroundPosition;
config$1.bgRepeat = config$1.backgroundRepeat;
config$1.bgAttachment = config$1.backgroundAttachment;
var background = /*#__PURE__*/system(config$1);

var config$2 = {
  border: {
    property: 'border',
    scale: 'borders'
  },
  borderWidth: {
    property: 'borderWidth',
    scale: 'borderWidths'
  },
  borderStyle: {
    property: 'borderStyle',
    scale: 'borderStyles'
  },
  borderColor: {
    property: 'borderColor',
    scale: 'colors'
  },
  borderRadius: {
    property: 'borderRadius',
    scale: 'radii'
  },
  borderTop: {
    property: 'borderTop',
    scale: 'borders'
  },
  borderTopLeftRadius: {
    property: 'borderTopLeftRadius',
    scale: 'radii'
  },
  borderTopRightRadius: {
    property: 'borderTopRightRadius',
    scale: 'radii'
  },
  borderRight: {
    property: 'borderRight',
    scale: 'borders'
  },
  borderBottom: {
    property: 'borderBottom',
    scale: 'borders'
  },
  borderBottomLeftRadius: {
    property: 'borderBottomLeftRadius',
    scale: 'radii'
  },
  borderBottomRightRadius: {
    property: 'borderBottomRightRadius',
    scale: 'radii'
  },
  borderLeft: {
    property: 'borderLeft',
    scale: 'borders'
  },
  borderX: {
    properties: ['borderLeft', 'borderRight'],
    scale: 'borders'
  },
  borderY: {
    properties: ['borderTop', 'borderBottom'],
    scale: 'borders'
  },
  borderTopWidth: {
    property: 'borderTopWidth',
    scale: 'borderWidths'
  },
  borderTopColor: {
    property: 'borderTopColor',
    scale: 'colors'
  },
  borderTopStyle: {
    property: 'borderTopStyle',
    scale: 'borderStyles'
  },
  borderBottomWidth: {
    property: 'borderBottomWidth',
    scale: 'borderWidths'
  },
  borderBottomColor: {
    property: 'borderBottomColor',
    scale: 'colors'
  },
  borderBottomStyle: {
    property: 'borderBottomStyle',
    scale: 'borderStyles'
  },
  borderLeftWidth: {
    property: 'borderLeftWidth',
    scale: 'borderWidths'
  },
  borderLeftColor: {
    property: 'borderLeftColor',
    scale: 'colors'
  },
  borderLeftStyle: {
    property: 'borderLeftStyle',
    scale: 'borderStyles'
  },
  borderRightWidth: {
    property: 'borderRightWidth',
    scale: 'borderWidths'
  },
  borderRightColor: {
    property: 'borderRightColor',
    scale: 'colors'
  },
  borderRightStyle: {
    property: 'borderRightStyle',
    scale: 'borderStyles'
  }
};
var border = /*#__PURE__*/system(config$2);

var config$3 = {
  backgroundColor: {
    property: 'backgroundColor',
    scale: 'colors'
  },
  color: {
    property: 'color',
    scale: 'colors'
  },
  opacity: {
    property: 'opacity'
  }
};
config$3.bgColor = config$3.backgroundColor;
var color = /*#__PURE__*/system(config$3);

var config$4 = {
  alignItems: {
    property: 'alignItems'
  },
  alignContent: {
    property: 'alignContent'
  },
  justifyItems: {
    property: 'justifyItems'
  },
  justifyContent: {
    property: 'justifyContent'
  },
  flexWrap: {
    property: 'flexWrap'
  },
  flexDirection: {
    property: 'flexDirection'
  },
  flex: {
    property: 'flex'
  },
  flexFlow: {
    property: 'flexFlow'
  },
  flexGrow: {
    property: 'flexGrow'
  },
  flexShrink: {
    property: 'flexShrink'
  },
  flexBasis: {
    property: 'flexBasis',
    scale: 'sizes'
  },
  justifySelf: {
    property: 'justifySelf'
  },
  alignSelf: {
    property: 'alignSelf'
  },
  order: {
    property: 'order'
  },
  placeItems: {
    property: 'placeItems'
  },
  placeContent: {
    property: 'placeContent'
  },
  placeSelf: {
    property: 'placeSelf'
  }
};
config$4.flexDir = config$4.flexDirection;
var flexbox = /*#__PURE__*/system(config$4);

var config$5 = {
  gridGap: {
    property: 'gridGap',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  gap: {
    property: 'gap',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  gridRowGap: {
    property: 'gridRowGap',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  rowGap: {
    property: 'rowGap',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  gridColumnGap: {
    property: 'gridColumnGap',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  columnGap: {
    property: 'columnGap',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  gridRow: {
    property: 'gridRow'
  },
  gridColumn: {
    property: 'gridColumn'
  },
  gridAutoFlow: {
    property: 'gridAutoFlow'
  },
  gridAutoRows: {
    property: 'gridAutoRows'
  },
  gridAutoColumns: {
    property: 'gridAutoColumns'
  },
  gridTemplateRows: {
    property: 'gridTemplateRows'
  },
  gridTemplateColumns: {
    property: 'gridTemplateColumns'
  },
  gridTemplateAreas: {
    property: 'gridTemplateAreas'
  },
  gridArea: {
    property: 'gridArea'
  }
};
var grid = /*#__PURE__*/system(config$5);

function getWidth(scale, n) {
  return get(scale, n, !isNumber(n) || n > 1 ? n : n * 100 + "%");
}

var config$6 = {
  width: {
    property: 'width',
    scale: 'sizes',
    transform: getWidth
  },
  height: {
    property: 'height',
    scale: 'sizes'
  },
  minWidth: {
    property: 'minWidth',
    scale: 'sizes'
  },
  maxWidth: {
    property: 'maxWidth',
    scale: 'sizes'
  },
  minHeight: {
    property: 'minHeight',
    scale: 'sizes'
  },
  maxHeight: {
    property: 'maxHeight',
    scale: 'sizes'
  },
  display: {
    property: 'display'
  },
  size: {
    properties: ['width', 'height'],
    scale: 'sizes'
  },
  verticalAlign: {
    property: 'verticalAlign'
  },
  overflow: {
    property: 'overflow'
  },
  overflowX: {
    property: 'overflowX'
  },
  overflowY: {
    property: 'overflowY'
  }
};
config$6.w = config$6.width;
config$6.h = config$6.height;
config$6.minW = config$6.minWidth;
config$6.maxW = config$6.maxWidth;
config$6.minH = config$6.minHeight;
config$6.maxH = config$6.maxHeight;
config$6.d = config$6.display;
var layout = /*#__PURE__*/system(config$6);

var config$7 = {
  appearance: {
    property: 'appearance'
  },
  transform: {
    property: 'transform'
  },
  transformOrigin: {
    property: 'transformOrigin'
  },
  visibility: {
    property: 'visibility'
  },
  userSelect: {
    property: 'userSelect'
  },
  pointerEvents: {
    property: 'pointerEvents'
  },
  overflowWrap: {
    property: 'overflowWrap'
  },
  boxSizing: {
    property: 'boxSizing'
  },
  cursor: {
    property: 'cursor'
  },
  resize: {
    property: 'resize'
  },
  objectFit: {
    property: 'objectFit'
  },
  objectPosition: {
    property: 'objectPosition'
  },
  "float": {
    property: 'float'
  },
  fill: {
    property: 'fill',
    scale: 'colors'
  },
  stroke: {
    property: 'stroke',
    scale: 'colors'
  },
  outline: {
    property: 'outline'
  },
  outlineColor: {
    property: 'outlineColor',
    scale: 'colors'
  }
};
var other = /*#__PURE__*/system(config$7);

var config$8 = {
  position: {
    property: 'position'
  },
  zIndex: {
    property: 'zIndex',
    scale: 'zIndices'
  },
  top: {
    property: 'top',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  right: {
    property: 'right',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  bottom: {
    property: 'bottom',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  left: {
    property: 'left',
    scale: 'space',
    defaultScale: defaultTheme.space
  }
};
config$8.pos = config$8.position;
var position = /*#__PURE__*/system(config$8);

var config$9 = {
  boxShadow: {
    property: 'boxShadow',
    scale: 'shadows'
  },
  textShadow: {
    property: 'textShadow',
    scale: 'shadows'
  }
};
var shadow = /*#__PURE__*/system(config$9);

function getMargin(scale, n) {
  if (!isNumber(n)) {
    return get(scale, n, n);
  }

  var isNegative = n < 0;
  var absolute = Math.abs(n);
  var value = get(scale, absolute, absolute);

  if (!isNumber(value)) {
    return isNegative ? "-" + value : value;
  }

  return value * (isNegative ? -1 : 1);
}
var marginConfig = {
  margin: {
    property: 'margin',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginTop: {
    property: 'marginTop',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginRight: {
    property: 'marginRight',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginBottom: {
    property: 'marginBottom',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginLeft: {
    property: 'marginLeft',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginX: {
    properties: ['marginLeft', 'marginRight'],
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginY: {
    properties: ['marginTop', 'marginBottom'],
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginBlockStart: {
    property: 'marginBlockStart',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginBlockEnd: {
    property: 'marginBlockEnd',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginInlineStart: {
    property: 'marginInlineStart',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  },
  marginInlineEnd: {
    property: 'marginInlineEnd',
    scale: 'space',
    transform: getMargin,
    defaultScale: defaultTheme.space
  }
};
marginConfig.m = marginConfig.margin;
marginConfig.mt = marginConfig.marginTop;
marginConfig.mr = marginConfig.marginRight;
marginConfig.mb = marginConfig.marginBottom;
marginConfig.ml = marginConfig.marginLeft;
marginConfig.mx = marginConfig.marginX;
marginConfig.my = marginConfig.marginY;
marginConfig.marginStart = marginConfig.marginInlineStart;
marginConfig.ms = marginConfig.marginInlineStart;
marginConfig.marginEnd = marginConfig.marginInlineEnd;
marginConfig.me = marginConfig.marginInlineEnd;
var margin = /*#__PURE__*/system(marginConfig);
var paddingConfig = {
  padding: {
    property: 'padding',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingTop: {
    property: 'paddingTop',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingRight: {
    property: 'paddingRight',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingBottom: {
    property: 'paddingBottom',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingLeft: {
    property: 'paddingLeft',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingX: {
    properties: ['paddingLeft', 'paddingRight'],
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingY: {
    properties: ['paddingTop', 'paddingBottom'],
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingBlockStart: {
    property: 'paddingBlockStart',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingBlockEnd: {
    property: 'paddingBlockEnd',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingInlineStart: {
    property: 'paddingInlineStart',
    scale: 'space',
    defaultScale: defaultTheme.space
  },
  paddingInlineEnd: {
    property: 'paddingInlineEnd',
    scale: 'space',
    defaultScale: defaultTheme.space
  }
};
paddingConfig.p = paddingConfig.padding;
paddingConfig.pt = paddingConfig.paddingTop;
paddingConfig.pr = paddingConfig.paddingRight;
paddingConfig.pb = paddingConfig.paddingBottom;
paddingConfig.pl = paddingConfig.paddingLeft;
paddingConfig.px = paddingConfig.paddingX;
paddingConfig.py = paddingConfig.paddingY;
paddingConfig.paddingStart = paddingConfig.paddingInlineStart;
paddingConfig.ps = paddingConfig.paddingInlineStart;
paddingConfig.paddingEnd = paddingConfig.paddingInlineEnd;
paddingConfig.pe = paddingConfig.paddingInlineEnd;
var padding = /*#__PURE__*/system(paddingConfig);
var space = /*#__PURE__*/compose(margin, padding);

var config$a = {
  transition: {
    property: 'transition',
    scale: 'transition.transition'
  },
  transitionProperty: {
    property: 'transitionProperty',
    scale: 'transition.property'
  },
  transitionDuration: {
    property: 'transitionDuration',
    scale: 'transition.duration'
  },
  transitionTiming: {
    property: 'transitionTimingFunction',
    scale: 'transition.timingFn'
  },
  transitionDelay: {
    property: 'transitionDelay',
    scale: 'transition.delay'
  }
};
var transition = /*#__PURE__*/system(config$a);

var config$b = {
  fontFamily: {
    property: 'fontFamily',
    scale: 'fonts'
  },
  fontSize: {
    property: 'fontSize',
    scale: 'fontSizes',
    defaultScale: defaultTheme.fontSizes
  },
  fontStyle: {
    property: 'fontStyle'
  },
  fontWeight: {
    property: 'fontWeight',
    scale: 'fontWeights'
  },
  letterSpacing: {
    property: 'letterSpacing',
    scale: 'letterSpacings'
  },
  lineHeight: {
    property: 'lineHeight',
    scale: 'lineHeights'
  },
  textAlign: {
    property: 'textAlign'
  },
  textDecoration: {
    property: 'textDecoration'
  },
  textOverflow: {
    property: 'textOverflow'
  },
  textTransform: {
    property: 'textTransform'
  },
  whiteSpace: {
    property: 'whiteSpace'
  },
  wordBreak: {
    property: 'wordBreak'
  }
};
var typography = /*#__PURE__*/system(config$b);

var all = /*#__PURE__*/compose(animation, background, border, color, flexbox, grid, layout, other, position, shadow, space, transition, typography);
function getParserDicts() {
  var aliases = {};
  var multiples = {};
  var scales = {};
  var transforms = {};
  all.propNames.forEach(function (propName) {
    var _config$transform;

    var config = all.config[propName];

    if (!config) {
      return;
    }

    if (config.properties) {
      multiples[propName] = [].concat(config.properties);
    }

    if (config.transform && ((_config$transform = config.transform) == null ? void 0 : _config$transform.name) !== 'getValue') {
      transforms[propName] = config.transform;
    }

    if (config.scale) {
      scales[propName] = config.scale;
    }

    if (config.property && propName !== config.property) {
      aliases[propName] = config.property;
    }
  });
  return {
    aliases: aliases,
    multiples: multiples,
    scales: scales,
    transforms: transforms
  };
}

var _getParserDicts = /*#__PURE__*/getParserDicts(),
    aliases = _getParserDicts.aliases,
    multiples = _getParserDicts.multiples,
    scales = _getParserDicts.scales,
    transforms = _getParserDicts.transforms;

function responsive(styles) {
  if (styles === void 0) {
    styles = {};
  }

  return function (theme) {
    var result = {};
    var breakpoints = get(theme, 'breakpoints', defaultBreakpoints);
    var mediaQueries = [null].concat(breakpoints.map(function (n) {
      return "@media screen and (min-width: " + n + ")";
    }));
    Object.keys(styles).forEach(function (key) {
      var value = isFunction(styles[key]) ? styles[key](theme) : styles[key];

      if (value === null) {
        return;
      }

      if (!Array.isArray(value)) {
        result[key] = value;
        return;
      }

      for (var i = 0; i < value.slice(0, mediaQueries.length).length; i += 1) {
        var media = mediaQueries[i];

        if (!media) {
          result[key] = value[i];
          continue;
        }

        result[media] = result[media] || {};

        if (value[i] == null) {
          continue;
        }

        result[media][key] = value[i];
      }
    });
    return result;
  };
}

function css(args) {
  return function (props) {
    var theme = _extends({}, defaultTheme, (props == null ? void 0 : props.theme) || props);

    var result = {};
    var obj = typeof args === 'function' ? args(theme) : args;
    var styles = responsive(obj)(theme);
    Object.keys(styles).forEach(function (key) {
      var x = styles[key];
      var val = isFunction(x) ? x(theme) : x;

      if (val && typeof val === 'object') {
        if (Object.keys(pseudoSelectors).includes(key)) {
          result[pseudoSelectors[key]] = css(val)(theme);
          return;
        }

        result[key] = css(val)(theme);
        return;
      }

      var prop = get(aliases, key, key);
      var scaleName = get(scales, prop);
      var scale = get(theme, scaleName, get(theme, prop, {}));
      var transform = get(transforms, prop, get);
      var value; // when the val is a number and less than 0, resolve it from the theme
      // using its absolute value and then make the result a negative.
      // otherwise, just resolve the value.

      if (typeof val === 'number' && val < 0) {
        var abs = Math.abs(val);
        var n = transform(scale, abs, abs);
        value = typeof n === 'string' ? "-" + n : n * -1;
      } else {
        value = transform(scale, val, val);
      }

      if (multiples[prop]) {
        var dirs = multiples[prop];

        for (var i = 0; i < Object.keys(dirs).length; i += 1) {
          result[dirs[i]] = value;
        }

        return;
      }

      result[prop] = value;
    });
    return result;
  };
}

var extendedConfig = {
  align: {
    property: 'alignItems'
  },
  justify: {
    property: 'justifyContent'
  },
  wrap: {
    property: 'flexWrap'
  },
  direction: {
    property: 'flexDirection'
  }
};
var extendedFlexbox = /*#__PURE__*/compose(flexbox, /*#__PURE__*/system(extendedConfig));

var extendedConfig$1 = {
  row: {
    property: 'gridRow'
  },
  column: {
    property: 'gridColumn'
  },
  autoFlow: {
    property: 'gridAutoFlow'
  },
  autoRows: {
    property: 'gridAutoRows'
  },
  autoColumns: {
    property: 'gridAutoColumns'
  },
  templateRows: {
    property: 'gridTemplateRows'
  },
  templateColumns: {
    property: 'gridTemplateColumns'
  },
  templateAreas: {
    property: 'gridTemplateAreas'
  },
  area: {
    property: 'gridArea'
  }
};
var extendedGrid = /*#__PURE__*/compose(grid, /*#__PURE__*/system(extendedConfig$1));

/**
 * Mixin that resolves to a CSS object containing all pseudo selector styles.
 *
 * Usage: `styled('div')(pseudoMixin)`
 */

var pseudoMixin = function pseudoMixin(props) {
  var pseudoKeys = Object.keys(pseudoSelectors);
  var result = {};
  Object.keys(props).forEach(function (key) {
    if (pseudoKeys.includes(key)) {
      result[key] = props[key];
    }
  });
  return css(result)(props);
};

var all$1 = /*#__PURE__*/compose(animation, background, border, color, extendedFlexbox, extendedGrid, flexbox, grid, layout, other, position, shadow, space, transition, typography);
/**
 * All `@spicy-ui/styled-system` props.
 */

var allProps = /*#__PURE__*/[].concat(all$1.propNames, /*#__PURE__*/Object.keys(pseudoSelectors));
/**
 * Create a custom `shouldForwardProp` function.
 * Use this when you don't want `@spicy-ui/styled-system` props included.
 */

function createShouldForwardProp(props) {
  var regex = new RegExp("^(" + props.join('|') + ")$");
  return memoize(function (prop) {
    return isPropValid(prop) && !regex.test(prop);
  });
}
/**
 * `shouldForwardProp` with all `@spicy-ui/styled-system` props.
 */

var shouldForwardProp = /*#__PURE__*/createShouldForwardProp(allProps);
/**
 * Creates a `shouldFowardProp` with all `@spicy-ui/styled-system` props.
 * Also allows you to add additional props to the `sfp` fn.
 */

function sfp(props) {
  return createShouldForwardProp([].concat(allProps, props));
}

export { allProps, animation, background, border, color, compose, createParser, createShouldForwardProp, createStyleFunction, css, extendedFlexbox, extendedGrid, flexbox, get, grid, layout, other, position, pseudoMixin, sfp, shadow, shouldForwardProp, space, system, transition, typography };
//# sourceMappingURL=styled-system.esm.js.map
